import { client } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  ContentItem,
  ContentInput,
  ContentListParams,
  ContentAdminListParams,
  ContentModerationLogEntry,
  PaginatedContent,
  UploadURLInput,
  UploadURLResponse,
  RegisterAssetInput,
  ContentMediaAsset,
  ContentSeedLink,
  ContentKPIs,
  ContentDailyCount,
  ContentDropoffBucket,
} from '@/types/content';

// SPEC-021: Content Management System — REST client. Mirrors the route
// shape defined by internal/api/router.go. Every function unwraps the
// `{ data }` envelope before returning.

function serializeParams(p?: Record<string, unknown>): Record<string, string | number | boolean> {
  if (!p) return {};
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v as string | number | boolean;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Authoring (teacher + admin)
// ---------------------------------------------------------------------------

export async function createContent(input: ContentInput): Promise<ContentItem> {
  const { data } = await client.post<ApiResponse<ContentItem>>('/api/v1/content', input);
  return data.data;
}

export async function updateContent(id: string, input: ContentInput): Promise<ContentItem> {
  const { data } = await client.put<ApiResponse<ContentItem>>(`/api/v1/content/${id}`, input);
  return data.data;
}

export async function getContent(id: string): Promise<ContentItem> {
  const { data } = await client.get<ApiResponse<ContentItem>>(`/api/v1/content/${id}`);
  return data.data;
}

export async function deleteContent(id: string): Promise<void> {
  await client.delete(`/api/v1/content/${id}`);
}

export async function submitContent(id: string): Promise<ContentItem> {
  const { data } = await client.post<ApiResponse<ContentItem>>(`/api/v1/content/${id}/submit`);
  return data.data;
}

export async function listMyContent(params?: ContentListParams): Promise<PaginatedContent> {
  const { data } = await client.get<PaginatedContent>('/api/v1/content/mine', {
    params: serializeParams(params as Record<string, unknown>),
  });
  return data;
}

// ---------------------------------------------------------------------------
// Media — presigned upload flow
// ---------------------------------------------------------------------------

export async function getUploadURL(
  contentId: string,
  input: UploadURLInput,
): Promise<UploadURLResponse> {
  const { data } = await client.post<ApiResponse<UploadURLResponse>>(
    `/api/v1/content/${contentId}/media/upload-url`,
    input,
  );
  return data.data;
}

export async function registerAsset(
  contentId: string,
  input: RegisterAssetInput,
): Promise<ContentMediaAsset> {
  const { data } = await client.post<ApiResponse<ContentMediaAsset>>(
    `/api/v1/content/${contentId}/media`,
    input,
  );
  return data.data;
}

export async function deleteAsset(contentId: string, assetId: string): Promise<void> {
  await client.delete(`/api/v1/content/${contentId}/media/${assetId}`);
}

/**
 * Uploads a file directly to S3 using a presigned PUT URL. Goes through the
 * global `fetch` rather than the axios client so the Authorization header
 * from the interceptor is not attached (presigned URLs reject any request
 * with an unexpected Authorization header).
 */
export async function uploadToPresignedURL(url: string, file: File): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: file.type ? { 'Content-Type': file.type } : undefined,
  });
  if (!res.ok) {
    throw new Error(`upload failed: ${res.status} ${res.statusText}`);
  }
}

// ---------------------------------------------------------------------------
// Seed links
// ---------------------------------------------------------------------------

export async function setSeedLinks(
  contentId: string,
  links: Array<Pick<ContentSeedLink, 'seed_id' | 'link_type'>>,
): Promise<void> {
  await client.post(`/api/v1/content/${contentId}/seed-links`, { links });
}

// ---------------------------------------------------------------------------
// Admin moderation
// ---------------------------------------------------------------------------

export async function adminListContent(
  params?: ContentAdminListParams,
): Promise<PaginatedContent> {
  const { data } = await client.get<PaginatedContent>('/api/v1/admin/content', {
    params: serializeParams(params as Record<string, unknown>),
  });
  return data;
}

export async function adminPendingContent(params?: {
  limit?: number;
  offset?: number;
}): Promise<PaginatedContent> {
  const { data } = await client.get<PaginatedContent>('/api/v1/admin/content/pending', {
    params: serializeParams(params as Record<string, unknown>),
  });
  return data;
}

export async function adminApprove(id: string): Promise<void> {
  await client.post(`/api/v1/admin/content/${id}/approve`);
}

export async function adminReject(id: string, reason: string): Promise<void> {
  await client.post(`/api/v1/admin/content/${id}/reject`, { reason });
}

export async function adminUnpublish(id: string): Promise<void> {
  await client.post(`/api/v1/admin/content/${id}/unpublish`);
}

export async function adminArchive(id: string): Promise<void> {
  await client.post(`/api/v1/admin/content/${id}/archive`);
}

export async function adminBulkAction(
  ids: string[],
  action: 'approve' | 'reject' | 'unpublish' | 'archive',
  reason?: string,
): Promise<{ success: number; failures: Record<string, string> }> {
  const { data } = await client.post<
    ApiResponse<{ success: number; failures: Record<string, string> }>
  >('/api/v1/admin/content/bulk-action', { ids, action, reason });
  return data.data;
}

export async function adminTrash(params?: {
  limit?: number;
  offset?: number;
}): Promise<PaginatedContent> {
  const { data } = await client.get<PaginatedContent>('/api/v1/admin/content/trash', {
    params: serializeParams(params as Record<string, unknown>),
  });
  return data;
}

export async function adminRestore(id: string): Promise<void> {
  await client.post(`/api/v1/admin/content/${id}/restore`);
}

export async function adminModerationLog(id: string): Promise<ContentModerationLogEntry[]> {
  const { data } = await client.get<ApiResponse<ContentModerationLogEntry[]>>(
    `/api/v1/admin/content/${id}/moderation-log`,
  );
  return data.data;
}

// ---------------------------------------------------------------------------
// Analytics (teacher + admin)
// ---------------------------------------------------------------------------

export async function getContentMetrics(id: string): Promise<ContentKPIs> {
  const { data } = await client.get<ApiResponse<ContentKPIs>>(
    `/api/v1/dashboard/content/${id}/metrics`,
  );
  return data.data;
}

export async function getContentTimeseries(
  id: string,
  range: '7d' | '30d' | '90d' = '30d',
): Promise<ContentDailyCount[]> {
  const { data } = await client.get<ApiResponse<ContentDailyCount[]>>(
    `/api/v1/dashboard/content/${id}/timeseries`,
    { params: { range } },
  );
  return data.data;
}

export async function getContentDropoff(id: string): Promise<ContentDropoffBucket[]> {
  const { data } = await client.get<ApiResponse<ContentDropoffBucket[]>>(
    `/api/v1/dashboard/content/${id}/dropoff`,
  );
  return data.data;
}
